import { useState, useEffect, useCallback } from 'react';
import { formatFranc, formatFrancAdaptive } from '../lib/utils';
import { teamAPI, entityAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { ArrowLeft, Crown, Trash2, TrendingUp, Shield, AlertTriangle, ChevronRight, Check } from 'lucide-react';
import { CaptainBadge } from '../components/CaptainBadge';
import DropPreview from '../components/DropPreview';
import FrancIcon from '../components/FrancIcon';
import { toast } from 'sonner';
import ConfirmDialog from '../components/ConfirmDialog';
import ShareButton from '../components/ShareButton';
import TeamNameInlineEditor from '../components/TeamNameInlineEditor';
import EntityAvatar from '../components/EntityAvatar';
import { getSlotLimits } from '../lib/slotConfig';
import { SharedScoreboardsNote } from '../components/SharedScoreboardsNote';
import { LeagueClosedNote } from '../components/LeagueClosedNote';

const ENTITY_TYPE_LABELS = {
  country_leader: 'Leader', ceo: 'CEO', actor: 'Actor',
  athlete: 'Athlete', musician: 'Artist', influencer: 'Influencer',
};

const AVATAR_COLORS = {
  country_leader: '#5a2d5e', ceo: '#1e3a5f', actor: '#c97a2e',
  athlete: '#2d7d5e', musician: '#b8860b', influencer: '#c94070',
};

export default function TeamManagement({ onBack, onEntityDetail, onTeamUpdate, teamId: passedTeamId, leagueId, leagueName, leagueSettings }) {
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);
  const [settingCaptain, setSettingCaptain] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null); // {entityId, name}
  // v5.7.56 — captain change is locked to once per gameweek; show
  // a confirmation dialog before sending the API call.
  const [confirmCaptain, setConfirmCaptain] = useState(null); // {entityId, name}

  const loadTeam = useCallback(async () => {
    try {
      const res = await teamAPI.myTeams();
      const teams = res.data.teams || [];
      // 2026-06 league audit — STRICT resolution: explicit teamId, then the
      // header-selected league; blind teams[0] only when neither is known.
      let pick = (passedTeamId && teams.find(t => t.id === passedTeamId)) || null;
      if (!pick && leagueId) pick = teams.find(t => t.league_id === leagueId) || null;
      if (!pick && !passedTeamId && !leagueId) pick = teams[0] || null;
      setTeam(pick);
      // Bubble fresh canonical team up so the app-wide currentTeam
      // doesn't go stale after drops/captain changes here (same defensive
      // wiring as Explore/Stage from v5.7.37).
      if (pick && onTeamUpdate) onTeamUpdate(pick);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [passedTeamId, leagueId, onTeamUpdate]);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  const handleSetCaptain = async (entityId) => {
    if (!team) return;
    setSettingCaptain(entityId);
    try {
      await teamAPI.setCaptain({ team_id: team.id, entity_id: entityId });
      toast.success('Captain updated! 2x multiplier applied.');
      await loadTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set captain');
    } finally { setSettingCaptain(null); }
  };

  const handleRemove = async (entityId, entityName) => {
    if (!team) return;
    setRemovingId(entityId);
    try {
      const res = await teamAPI.remove({ team_id: team.id, entity_id: entityId });
      const penaltyMsg = res.data.penalty > 0
        ? ` (penalty ${formatFranc(res.data.penalty)})`
        : '';
      toast.success(`Removed ${entityName}. Refund ${formatFranc(res.data.refund)}${penaltyMsg}`);
      setConfirmRemove(null);
      await loadTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove');
    } finally { setRemovingId(null); }
  };

  // Roster-move fee ladder now lives in lib/rosterMoveMath (shared with
  // DraftRoom's post-finalize Add disclosure).

  const slots = team?.slots || [];
  const categoryCounts = {};
  slots.forEach(s => { categoryCounts[s.entity_type] = (categoryCounts[s.entity_type] || 0) + 1; });
  // 2026-08-14 — roster shape follows the league's slot_config (a custom
  // "CEOs only" league is 18 CEO slots, not 6 hardcoded categories × 3).
  // Falls back to the canonical 18 when the league has no custom config.
  const slotLimits = getSlotLimits(leagueSettings);
  const rosterCap = slotLimits.total;
  // Captain picks unlock once the roster is FULL (pre-finalize). Post-
  // finalize, captain changes stay available (deaths/transfers can shrink
  // a roster below cap).
  const captainUnlocked = !!team?.finalized || slots.length >= rosterCap;
  // WEALTH MODEL — Net Worth = cash + roster value (Σ current entity prices),
  // matching the leaderboard ranking metric / GlobalHeader pill.
  // Quarterly mirror: quarter-relative, i.e. minus its quarter-start offset.
  const netWorth = (team?.budget_remaining || 0)
    + slots.reduce((sum, s) => sum + (s.entity?.pricing?.price || 0), 0)
    - (team?.quarter_offset || 0);

  const removeDialogDescription = (() => {
    if (!confirmRemove) return '';
    const finalized = team?.finalized;
    // Refund is the CURRENT MARKET price (buy low / sell high), matching the
    // backend remove_entity. `confirmRemove` is a roster slot.
    const marketPrice = confirmRemove.entity?.pricing?.price ?? confirmRemove.pricing?.price;
    const marketStr = typeof marketPrice === 'number' ? `${formatFranc(marketPrice)} (current market price)` : 'their current market price';
    if (!finalized) {
      // Build 4 — pre-finalize refunds are locked at the ACQUIRED price
      // (exactly what was paid), matching the backend exploit fix.
      const paid = confirmRemove.acquired_price;
      const paidStr = typeof paid === 'number' ? `${formatFranc(paid)} — exactly what you paid` : 'exactly what you paid';
      return `Remove ${confirmRemove.name} from your team? You'll be refunded ${paidStr} (no penalty during the draft phase).`;
    }
    // 2026-09-03 (owner decision) — drops are FREE; the fee ladder is
    // charged once per replacement, on the add.
    return `Remove ${confirmRemove.name}? Dropping is free — you'll be refunded ${marketStr} and it won't count as a roster move. Only adding a replacement counts toward this week's move fees.`;
  })();

  if (loading) {
    return (
      <div className="p-4 space-y-3">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-20 w-full rounded-2xl" />
        {[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full rounded-2xl" />)}
      </div>
    );
  }

  return (
    <div className="p-4" data-testid="team-management">
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" onClick={onBack} data-testid="team-mgmt-back">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <h2 className="text-base font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>
          {leagueName ? `${leagueName} — Manage` : 'Team Management'}
        </h2>
        {team?.id ? (
          <ShareButton
            path={`/team/${team.id}`}
            imagePath={`/api/share/team/${team.id}/card.png`}
            title={team.name ? `${team.name} on Fantasy World Star` : 'My team'}
            text={team.name ? `Check out my ${team.name} fantasy roster` : 'Check out my fantasy roster'}
            iconOnly
            testid="share-team-btn"
          />
        ) : <div className="w-9" />}
      </div>

      {/* Team Summary */}
      {team && (
        <div className="bg-card border border-border/60 rounded-2xl p-4 mb-4">
          <div className="mb-3">
            <TeamNameInlineEditor
              team={team}
              onRenamed={(updated) => setTeam({ ...team, name: updated.name })}
            />
            <SharedScoreboardsNote team={team} testid="team-shared-scoreboards-note" className="mt-1.5" />
            <LeagueClosedNote team={team} testid="team-league-closed-note" className="mt-2" />
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Roster</p>
              <p className="text-lg font-bold tabular-nums" data-testid="team-roster-count">{slots.length}/{rosterCap}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Budget</p>
              <p className="text-lg font-bold tabular-nums" data-testid="team-budget">{formatFranc(team.budget_remaining)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Net Worth</p>
              <p className="text-lg font-bold tabular-nums" data-testid="team-networth">{formatFrancAdaptive(netWorth)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Moves used</p>
              <p className="text-lg font-bold tabular-nums">{team.weekly_transfers_used || 0}<span className="text-xs font-normal text-muted-foreground"> this wk</span></p>
            </div>
          </div>

          {/* Category fill status — denominators follow the league's
              slot_config; 0-cap categories are hidden entirely. */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {slotLimits.activeCategories.map((type) => {
              const label = ENTITY_TYPE_LABELS[type];
              const count = categoryCounts[type] || 0;
              const cap = slotLimits.perCategory(type);
              const full = count >= cap;
              return (
                <Badge
                  key={type}
                  variant={full ? 'default' : 'secondary'}
                  data-testid={`team-cat-badge-${type}`}
                  className={`text-[9px] ${full ? 'bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/20' : ''}`}
                >
                  {label}: {count}/{cap} {full && <Check className="h-2.5 w-2.5 ml-0.5" />}
                </Badge>
              );
            })}
          </div>

          {/* Captain timing — no captain picks until the roster is full
              (finalize forces the pick anyway). */}
          {!captainUnlocked && slots.length > 0 && (
            <p className="text-[10px] text-muted-foreground mt-2" data-testid="captain-locked-hint">
              <Crown className="h-3 w-3 inline mr-1 text-yellow-500" />
              Captain picks unlock once your roster is full ({slots.length}/{rosterCap}).
            </p>
          )}
        </div>
      )}

      {/* Unified roster-move penalty schedule (v5.7.33) — both Transfer
          and Remove share one weekly counter and one penalty curve. */}
      <div className="bg-secondary/50 border border-border/40 rounded-xl p-3 mb-4 text-xs text-muted-foreground">
        <AlertTriangle className="h-3.5 w-3.5 inline mr-1 text-yellow-500" />
        Roster move penalties (Transfer or Remove): 1st free • 2nd: 2% budget • 3rd+: 5%+ budget (cap 90%)
        <span className="ml-2 font-medium">Used this week: {team?.weekly_transfers_used || 0}</span>
      </div>

      {/* Entity List */}
      <ScrollArea className="h-[calc(100vh-420px)]">
        <div className="space-y-2">
          {slots.length === 0 && (
            <div className="text-center py-12 text-muted-foreground text-sm">
              No people on team. Go to Draft Room to build your roster!
            </div>
          )}
          {slots.map((slot) => {
            const entity = slot.entity;
            return (
              <div
                key={slot.entity_id}
                className="flex items-center gap-3 bg-card border border-border/60 rounded-2xl px-4 py-3"
              >
                <div
                  className="relative shrink-0 cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${entity?.name || 'person'} details`}
                  onClick={() => onEntityDetail && onEntityDetail(slot.entity_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onEntityDetail && onEntityDetail(slot.entity_id);
                    }
                  }}
                >
                  <EntityAvatar entity={entity} size={40} />
                  {slot.is_captain && <CaptainBadge className="-top-1 -right-1" />}
                </div>
                <div
                  className="flex-1 min-w-0 cursor-pointer rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${entity?.name || 'person'} details`}
                  onClick={() => onEntityDetail && onEntityDetail(slot.entity_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onEntityDetail && onEntityDetail(slot.entity_id);
                    }
                  }}
                >
                  <p className="text-sm font-medium truncate">{entity?.name || 'Unknown'}</p>
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <span>{ENTITY_TYPE_LABELS[slot.entity_type]}</span>
                    <span>• Bought: {formatFranc(slot.acquired_price)}</span>
                    <span>• Now: {formatFranc(entity?.pricing?.price)}</span>
                  </div>
                </div>
                {!team?.frozen && (
                <div className="flex items-center gap-1 shrink-0">
                  {/* 2026-08-14 — "Set as Captain" hidden until the roster is
                      full (premature captain picks broke the draft sequence);
                      the current captain's green pill always shows. */}
                  {(slot.is_captain || captainUnlocked) && (
                  <Button
                    size="sm" variant="outline"
                    className={`h-7 text-[10px] gap-1 border ${slot.is_captain
                      ? 'bg-green-500/15 text-green-600 border-green-500/50 hover:bg-green-500/15 disabled:opacity-100'
                      : 'bg-yellow-400/15 text-yellow-600 border-yellow-500/50 hover:bg-yellow-400/25'}`}
                    onClick={() => setConfirmCaptain({ entityId: slot.entity_id, name: entity?.name })}
                    disabled={settingCaptain === slot.entity_id || slot.is_captain}
                    data-testid={`team-captain-${slot.entity_id}`}
                  >
                    <Crown className="h-3 w-3" />
                    {slot.is_captain ? 'Captain' : 'Set as Captain'}
                  </Button>
                  )}
                  <Button
                    size="sm" variant="ghost" className="h-7 text-[10px] text-destructive hover:text-destructive"
                    onClick={() => setConfirmRemove({ entityId: slot.entity_id, name: entity?.name, acquired_price: slot.acquired_price })}
                    disabled={removingId === slot.entity_id}
                    data-testid={`team-remove-${slot.entity_id}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {/* Remove confirmation dialog — copy is dynamic and matches the
          unified Transfer-style penalty schedule (1st free, 2nd 2%, 3rd+
          5%+ budget). */}
      <ConfirmDialog
        open={!!confirmRemove}
        title="Remove Person"
        description={removeDialogDescription}
        extra={confirmRemove ? <DropPreview teamId={team?.id} entityId={confirmRemove.entityId} /> : null}
        confirmLabel="Remove"
        cancelLabel="Keep"
        variant="destructive"
        onConfirm={() => confirmRemove && handleRemove(confirmRemove.entityId, confirmRemove.name)}
        onCancel={() => setConfirmRemove(null)}
      />

      {/* v5.7.56 — captain set confirmation. Captain can only be changed
          ONCE per gameweek (backend returns 429 otherwise) so this is a
          no-takebacks decision until next week. */}
      <ConfirmDialog
        open={!!confirmCaptain}
        title="Set Captain?"
        description={confirmCaptain ? (
          `Make ${confirmCaptain.name} your Captain? Your Captain earns a 2× multiplier on every ₣ they generate this week. ` +
          `Heads up: you can only change Captain ONCE per gameweek — once you confirm, the next change unlocks at the start of next gameweek.`
        ) : ''}
        confirmLabel="Yes, set as Captain"
        cancelLabel="Cancel"
        onConfirm={() => {
          if (confirmCaptain) {
            const id = confirmCaptain.entityId;
            setConfirmCaptain(null);
            handleSetCaptain(id);
          }
        }}
        onCancel={() => setConfirmCaptain(null)}
      />
    </div>
  );
}
